import "./index.less";
import React, { useEffect } from "react";
import { useUpdateEffect, usePrevious } from "ahooks";
import SearchPane from "./SearchPane";
import ListOptionsPane from "./ListOptionsPane";
import AddressDeliverableCheckPane from "./AddressDeliverableCheckPane";
import LoadingMarkContainer from "../../../../components/LoadingMarkContainer";
import Tabs from "./Tabs";
import ShopDTO from "@/dto/shop/ShopDTO";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import FulfillmentTypeSelect from "./ListOptionsPane/FulfillmentTypeSelect";
import { observer } from "mobx-react-lite";
import OrderTipLine from "./OrderTipLine";
import ShopType from "@/enum/ShopType";
import { useOrderManagementStore } from "../../store/OrderManagementStore";
import RefundOrderSelect from "./ListOptionsPane/RefundOrderSelect";
import OrderTableColumnsType from "../../enum/OrderTableColumnsType";
import HistoryOrderHint from "./HistoryOrderHint";
import { useGlobalStore } from "@/store/hooks";
import { getOrderSort } from "../../redux/sagas/localConfig";
import FulfillmentType from "@/enum/FulfillmentType";
import PreShipSetting from "./PreShipSetting";
import ShippingDeadlineCount from "./ShippingDeadlineCount";
import { useModalStore } from "@/hooks/Modal/hooks";
import OrderManagementBeginnerGuideModal from "@/scenes/OrderManagement/components/BeginnerGuideModal";
import VirtualizedTableWrap from "./VirtualizedTableWarp";
import { showDeadlineShippingOrderTabs } from "@/utils/OrderTabUtil";
import ShippingOverdueCount from "./ShippingOverdueCount";
import useLoadOrderListCountOnMount from "./hooks/useLoadOrderListCountOnMount";
import { useDropshippingEffect } from "../../hooks/useDropshippingEffect";
import LogisticsWarningSelect from "./ListOptionsPane/LogisticsWarningSelect";
import { shouldShowDropShippingDetail } from "@/utils/ShopUtils";

interface OrderTableProps {
    shop: ShopDTO;
    tab: OrderTab;
    isSupplierShop: boolean;
}

const OrderTable: React.FC<OrderTableProps> = (props) => {
    const { shop, tab, isSupplierShop } = props;

    const modalStore = useModalStore();
    const globalStore = useGlobalStore();
    const orderManagementStore = useOrderManagementStore();
    const { orders } = orderManagementStore.table;

    const prevProps = usePrevious(Object.assign({}, props));

    useDropshippingEffect();

    /** 新手操作引导 */
    useEffect(() => {
        const search = new URLSearchParams(location.search);
        const showBeginner = search.get("show_beginner");

        if (showBeginner) {
            modalStore.openModal(OrderManagementBeginnerGuideModal, {
                targets: [
                    {
                        selector: ".order-table-select-all-order-checkbox",
                        title: "1.批量勾选订单或勾选单个订单",
                    },
                    {
                        selector: ".ec-print-waybill-button",
                        title: "2.打印快递单",
                    },
                ],
            });
        }
    }, []);

    useEffect(() => {
        orderManagementStore.fetchOrders(shop.id, tab);
    }, []);

    useLoadOrderListCountOnMount(shop, tab);

    useUpdateEffect(() => {
        async function handleShopOrTabChange() {
            if (shop.id !== prevProps.shop.id) {
                orderManagementStore.table.resetFormValues();
                orderManagementStore.table.changePageNo(1);
                orderManagementStore.table.changeFulfillmentType(FulfillmentType.ALL);
                const orderSort = await getOrderSort(shop.id, tab);
                orderManagementStore.table.changeOrderSort(shop.id, tab, orderSort);

                orderManagementStore.fetchOrders(shop.id, tab);
                orderManagementStore.getOrderListCount(shop.id, tab);
            } else if (tab !== prevProps.tab) {
                orderManagementStore.table.resetFormValues();
                orderManagementStore.table.changePageNo(1);

                orderManagementStore.fetchOrders(shop.id, tab);
                const orderSort = await getOrderSort(shop.id, tab);
                orderManagementStore.table.changeOrderSort(shop.id, tab, orderSort);

                const refundingOrderTabs = [OrderTab.SHIPPED_REFUNDING, OrderTab.PENDING_SHIPMENT_REFUNDING];

                if (refundingOrderTabs.includes(tab) || showDeadlineShippingOrderTabs.includes(tab)) {
                    orderManagementStore.getOrderListCount(shop.id, tab);
                }
            }
        }

        handleShopOrTabChange();
    }, [shop, tab]);

    const fulfillmentColumn = orderManagementStore.storage.orderTableColumns.find(
        (column) => column.type === OrderTableColumnsType.SUPPLIER_DROPSHIPPING2,
    );
    const isShowFulfillment = fulfillmentColumn?.visible;
    const canShowFulfillmentSelect =
        !isSupplierShop &&
        (Boolean(fulfillmentColumn) || (shouldShowDropShippingDetail(shop) && globalStore.companyRoleStore.isRetailer));
    const establishedPartnerships = orderManagementStore.retailerPartnershipsStore.establishedPartnershipsMap.get(
        shop.id,
    );
    const isShowFulfillmentSelect = canShowFulfillmentSelect && Boolean(establishedPartnerships?.length);

    useEffect(() => {
        if (!canShowFulfillmentSelect) return;

        orderManagementStore.retailerPartnershipsStore.ensureEstablishedPartnerships(shop.id);
    }, [canShowFulfillmentSelect, shop.id]);

    const isShowLogisticsWarningSelect =
        [ShopType.PDD_FDS, ShopType.PINDUODUO].includes(shop.type) &&
        [OrderTab.LOGISTICS_WARNING, OrderTab.PICKUP_DEADLINE_WARNING].includes(tab);

    useEffect(() => {
        globalStore.shopAdvancedSettingStore.getAdvancedSetting(shop.id);
    }, [shop.id]);

    useEffect(() => {
        if (![ShopType.DOUDIAN, ShopType.CHANNEL_SHOP].includes(shop?.type)) {
            orderManagementStore.table.calcDoubleAddress(orderManagementStore.table.orders);
        }
    }, [orderManagementStore.table.orders]);

    return (
        <div className="order-table-container">
            <Tabs shop={shop} tab={tab} isSupplierShop={isSupplierShop} />

            {tab === OrderTab.PRE_SHIP ? <PreShipSetting shop={shop} /> : null}

            <OrderTipLine tab={tab} />

            <div className="order-table-shipping-counts">
                <ShippingDeadlineCount shop={shop} tab={tab} />

                <ShippingOverdueCount shop={shop} tab={tab} />
            </div>

            <div className="flex">
                <RefundOrderSelect shopId={shop.id} orderTab={tab} />

                {/* 有分销关系的代理商显示 */}
                {isShowFulfillmentSelect && <FulfillmentTypeSelect shop={shop} tab={tab} />}

                {isShowLogisticsWarningSelect && <LogisticsWarningSelect shop={shop} tab={tab} />}
            </div>

            {!isShowFulfillmentSelect && <HistoryOrderHint shop={shop} tab={tab} />}

            <SearchPane shopId={shop.id} tab={tab} />

            <ListOptionsPane shop={shop} tab={tab} />

            <AddressDeliverableCheckPane />

            <LoadingMarkContainer>
                <VirtualizedTableWrap
                    tab={tab}
                    shop={shop}
                    isSupplierShop={isSupplierShop}
                    isShowFulfillment={isShowFulfillment}
                />
            </LoadingMarkContainer>
        </div>
    );
};

export default observer(OrderTable);
